import { Button, Card, Message, Select, Space, Table, Grid } from "@arco-design/web-react";
import { useCallback, useEffect, useState } from "react";
import { CustomerAPI, SOPOAPI } from "@/apis/sales";
import { ItemAPI } from "@/apis/material";
import { Number } from "@/components/Numeric";
import { UserProfile, UserProfileType, FileUtil } from "@/utils";
import { SOPOStatus } from "@/enums/sales";
import _ from "lodash";
import { Link } from "react-router-dom";
import Reference from "@/components/Reference";

const Row = Grid.Row;
const Col = Grid.Col;

const ColumnsOptions = [
    "Pick#",
    "Customer",
    "Reference#",
    "SO Due Date",
    "SO Ready Date",
    "Item Number",
    "Item Name",
    "Item Description",
    "SO Open Qty",
    "Pick Qty"
];

const Columns = [
    {
        title: "Pick#",
        render: (cols, record) => {
            return <Link to={`/workspace/sales/so-pick-order/${record.soPickOrderId}`}>{record.pickOrder?.pickOrderNo}</Link>
        },
        width: 115
    }, {
        title: "Customer",
        dataIndex: "pickOrder.customerName"
    }, {
        title: "Reference#",
        render: (col, record) => <Reference value={record.pickOrder?.saleOrder?.reference} />,
    }, {
        title: "SO Due Date",
        dataIndex: "pickOrder.soDueDate",
        width: 105
    }, {
        title: "SO Ready Date",
        dataIndex: "pickOrder.readyDate",
        width: 105
    }, {
        title: "Item Number",
        dataIndex: "itemNo"
    }, {
        title: "Item Name",
        dataIndex: "itemName"
    }, {
        title: "Item Description",
        dataIndex: "description"
    }, {
        title: "SO Open Qty",
        render: (cols, record) => <Number value={record.soOpenQty} />
    }, {
        title: "Pick Qty",
        render: (cols, record) => <Number value={record.pickQty} />
    }]

export const SOPickingReport = () => {
    const [selectedColumnNames, setSelectedColumnNames] = useState(ColumnsOptions);
    const [columns, setColumns] = useState(Columns);
    const [items, setItems] = useState([]);
    const [itemIds, setItemIds] = useState();
    const [customers, setCustomers] = useState([]);
    const [customerIds, setCustomerIds] = useState();
    const [data, setData] = useState([]);
    const [pagination, setPagination] = useState({
        current: 1,
        total: 0,
    });
    const [status, setStatus] = useState(SOPOStatus.Open);
    const [totalQuantity, setTotalQuantity] = useState(0);

    const getData = useCallback(async (page, customerIds, itemIds, status) => {
        const res = await SOPOAPI.report({
            page,
            customerIds,
            itemIds,
            status
        });
        setData(res.items.records);
        setPagination((pagination) => {
            return {
                ...pagination,
                total: res.items.total,
            }
        });
        setTotalQuantity(res.totalQuantity);
    }, []);

    const saveProfile = () => {
        UserProfile.saveProfile(UserProfileType.SO_PICKING_REPORT, {
            columns: selectedColumnNames
        });
    }

    useEffect(() => {
        const getCustomersAndItems = async () => {
            const customers = await CustomerAPI.actives();
            const sortedCustomers = _.sortBy(customers, "customerName");
            setCustomers(sortedCustomers);
            const items = await ItemAPI.actives();
            const sortedItems = _.sortBy(items, "itemName");
            setItems(sortedItems);

        }
        getCustomersAndItems();
    }, [])

    useEffect(() => {
        const loadColumns = async () => {
            const cols = Columns.filter(col => selectedColumnNames.includes(col.title));
            setColumns(cols);
        }

        loadColumns();
    }, [selectedColumnNames])

    useEffect(() => {
        const load = async () => {
            const profile = await UserProfile.getProfile(UserProfileType.SO_PICKING_REPORT);
            if (profile) {
                profile.columns && setSelectedColumnNames(profile.columns);
            }
        };
        load();
    }, [])

    useEffect(() => {
        getData(1, customerIds, itemIds, status);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getData])

    return (
        <Card title="SO Picking Report" extra={<Space>
            <Select
                mode="multiple"
                showSearch
                maxTagCount={1}
                filterOption={(inputValue, option) =>
                    option.props.children.toLowerCase().indexOf(inputValue.toLowerCase()) >= 0
                }
                options={customers.map((customer) => {
                    return {
                        label: customer.customerName,
                        value: customer.id
                    }
                })}
                placeholder="Select Customers"
                allowClear
                style={{ width: 200 }}
                value={customerIds}
                onChange={(value) => {
                    setCustomerIds(value);
                }}
            />
            <Select
                mode="multiple"
                showSearch
                maxTagCount={1}
                options={_.map(items, (item) => {
                    return {
                        label: item.itemName,
                        value: item.id
                    }
                })}
                placeholder="Select Items"
                allowClear
                style={{ width: 200 }}
                value={itemIds}
                filterOption={(inputValue, option) =>
                    option.props.children.toLowerCase().indexOf(inputValue.toLowerCase()) >= 0
                }
                onChange={(value) => {
                    setItemIds(value);
                }}
            />
            <Select
                placeholder="Select Status"
                allowClear
                style={{ width: 130 }}
                options={[...Object.entries(SOPOStatus).map(([label, value]) => {
                    return {
                        label: label,
                        value: value
                    }
                })]}
                value={status}
                onChange={(value) => {
                    setStatus(value);
                }}
            />
            <Button type="primary" onClick={() => {
                getData(1, customerIds, itemIds, status);
            }} >Search</Button>
            <Button type="primary" onClick={async () => {
                const res = await SOPOAPI.exportReport({
                    customerIds,
                    itemIds,
                    status
                });
                FileUtil.download(res.data, "SO Picking Report.xlsx");
            }} >Export</Button>
            <Button type="primary" onClick={async () => {
                await saveProfile();
                Message.success("Profile Saved");
            }} >Save Profile</Button>
        </Space>}>
            <Row justify="end">
                <Col flex="auto">
                    <span>Total Qty: <Number value={totalQuantity} /></span>
                </Col>
                <Col flex="100px">
                    <Select
                        mode="multiple"
                        bordered={false}
                        placeholder="Columns"
                        triggerProps={{
                            autoAlignPopupWidth: false,
                            autoAlignPopupMinWidth: true,
                        }}
                        value={selectedColumnNames}
                        renderTag={(props, index) => {
                            return index === 0 ? <span style={{ margin: "0 5px" }}>Columns</span> : null;
                        }}
                        onChange={(selected) => {
                            setSelectedColumnNames(selected);
                        }}
                        options={ColumnsOptions}
                    />
                </Col>
            </Row>
            <Table rowKey="id"
                columns={columns}
                scroll={{ y: 'calc(100vh - 320px)' }}
                data={data}
                pagination={pagination}
                onChange={(pagination) => {
                    setPagination(pagination);
                    getData(pagination.current, customerIds, itemIds, status);
                }}
            />
        </Card>
    );
}